import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { NumericInput } from "@blueprintjs/core";
import PropTypes from 'prop-types';
import { FormHelperText } from "components/form-fields";
import 'components/form-fields/FormFields.css';
import { ShowHide } from 'components/layout';

export function FormNumericInput(
    {
        id = null,
        selectedValue = null,
        loading = false,
        headingText = "",
        helperText = null,
        disabled = false,
        disabledInput = false,
        max = null,
        min = null,
        dangerHelperText = null,
        allowDecimal = false,
        decimalPlaces = 2,
        prefixText = null,
        fill = false,
        unit = "",
        placeholder = "",
        onValueChange
    }
) {
    const [selectedValueValue, setSelectedValue] = useState("");
    const inputRef = useRef(null);
    const [hasUnit, setHasUnit] = useState(false);

    useEffect(() => {
        setHasUnit((unit?.length ?? 0) > 0);
    }, [unit])

    useEffect(() => {
        const hasValue = selectedValue || selectedValue === 0;
        let value = hasValue ? selectedValue : "";

        // Only a clamp changes the value, so only a clamp is reported back to the parent.
        if (hasValue && max != null && value > max) {
            value = max;
            onValueChange(value);
        }

        setSelectedValue(value);
    }, [selectedValue]);

    useEffect(() => {
        if (inputRef.current != null) {
            inputRef.current.disabled = disabledInput;
        }
    }, [inputRef, disabledInput])

    function onInputRefChange(ref) {
        inputRef.current = ref;
    }

    function onValueChangeValue(value, valueString) {
        // If decimals are allowed, then we must return the string
        // representation of the value. This is to make sure that
        // when the user types '2.' (for example) it is not turned
        // back into '2', preventing them from adding the decimal.

        //strip 'e' from input which is allowed as scientific notation
        if (/e/i.test(valueString)) return;

        valueString = valueString.trim();

        //Ensure negative numbers are handled correctly
        const hasLeadingMinus = valueString.startsWith("-");

        if (valueString.lastIndexOf("-") > 0) {
            const cleanedValue = valueString.replace(/-/g, "");
            if (hasLeadingMinus) {
                valueString = "-" + cleanedValue;
            }

            value = parseFloat(valueString);
        }

        //Ensure decimal numbers are handled correctly
        const firstDotIndex = valueString.indexOf(".");

        if (firstDotIndex !== -1) {
            const withoutDots = valueString.replace(/\./g, "");

            valueString = allowDecimal ?
                withoutDots.slice(0, firstDotIndex) + "." + withoutDots.slice(firstDotIndex) : withoutDots;
        }

        const shouldUseRawString =
            valueString === "" ||
            allowDecimal ||
            (hasLeadingMinus && isNaN(value));

        onValueChange(shouldUseRawString ? valueString : value);
    }

    return (
        <div className="form-field">
            <ShowHide
                evaluator={headingText.length > 0}
                show={(
                    <h4 className={classNames({ "bp6-skeleton": loading })}>{headingText}</h4>
                )}
            >
            </ShowHide>
            <FormHelperText loading={loading}>{helperText}</FormHelperText>
            <div className={hasUnit ? 'inline-items' : ''}>
            <NumericInput
                id={id}
                disabled={disabled}
                className={classNames("small", { "bp6-skeleton": loading, "disabled-input-box": disabledInput && !disabled, "fill": fill })}
                onValueChange={onValueChangeValue}
                value={selectedValueValue}
                max={max}
                min={min}
                inputRef={onInputRefChange}
                minorStepSize={!allowDecimal ? null : (1 / Math.pow(10, decimalPlaces))}
                leftIcon={prefixText == null ? null : <span className="form-numeric-icon">{prefixText}</span>}
                placeholder={placeholder}
            ></NumericInput>
            {hasUnit && <span>{unit}</span>}
            </div>
            <FormHelperText loading={loading} danger={true}>{dangerHelperText}</FormHelperText>
        </div>
    );
}

FormNumericInput.propTypes = {
    id: PropTypes.string,
    selectedValue: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    loading: PropTypes.bool,
    headingText: PropTypes.string,
    helperText: PropTypes.node,
    disabled: PropTypes.bool,
    disabledInput: PropTypes.bool,
    onValueChange: PropTypes.func.isRequired,
    max: PropTypes.number,
    min: PropTypes.number,
    dangerHelperText: PropTypes.node,
    allowDecimal: PropTypes.bool,
    decimalPlaces: PropTypes.number,
    prefixText: PropTypes.string,
    fill: PropTypes.bool,
    unit: PropTypes.string,
    placeholder: PropTypes.string
};